import { type Static, Type } from "@sinclair/typebox"
import { createPageSchema, IsoDateTimeSchema, JsonRecordSchema, Nullable } from "./common"

// Webhooks [V1] — docs/04 §4. Firma X-Rema-Signature: t=<unix>,v1=<hmac-sha256>.

// Eventos suscribibles (catálogo exacto de docs/04 §4).
export const WEBHOOK_EVENTS = [
  "user.created",
  "user.registered",
  "user.locked",
  "user.deleted",
  "domain.created",
  "domain.suspended",
  "shard.assigned",
  "shard.reassigned",
  "auth.refresh.reused",
  "node.down",
] as const
export type WebhookEventName = (typeof WEBHOOK_EVENTS)[number]

export const WebhookEventNameSchema = Type.Union(WEBHOOK_EVENTS.map((event) => Type.Literal(event)))

// `disabled` se alcanza manualmente o tras 50 fallos consecutivos.
export const WebhookStatusSchema = Type.Union([Type.Literal("active"), Type.Literal("disabled")])
export type WebhookStatus = Static<typeof WebhookStatusSchema>

export const WebhookSchema = Type.Object({
  id: Type.String(),
  url: Type.String(),
  events: Type.Array(WebhookEventNameSchema),
  status: WebhookStatusSchema,
  failure_count: Type.Integer({ minimum: 0 }),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type Webhook = Static<typeof WebhookSchema>

export const WebhookPageSchema = createPageSchema(WebhookSchema)
export type WebhookPage = Static<typeof WebhookPageSchema>

export const CreateWebhookRequestSchema = Type.Object({
  url: Type.String({ minLength: 1 }),
  events: Type.Array(WebhookEventNameSchema, { minItems: 1 }),
})
export type CreateWebhookRequest = Static<typeof CreateWebhookRequestSchema>

// El secret de firma solo se devuelve una vez, al crear.
export const CreateWebhookResponseSchema = Type.Composite([
  WebhookSchema,
  Type.Object({ secret: Type.String() }),
])
export type CreateWebhookResponse = Static<typeof CreateWebhookResponseSchema>

export const UpdateWebhookRequestSchema = Type.Object({
  url: Type.Optional(Type.String({ minLength: 1 })),
  events: Type.Optional(Type.Array(WebhookEventNameSchema, { minItems: 1 })),
  status: Type.Optional(WebhookStatusSchema),
})
export type UpdateWebhookRequest = Static<typeof UpdateWebhookRequestSchema>

// POST /admin/webhooks/{id}/test — entrega un evento de prueba y reporta el resultado.
export const TestWebhookResponseSchema = Type.Object({
  delivered: Type.Boolean(),
  status_code: Nullable(Type.Integer()),
  error: Nullable(Type.String()),
})
export type TestWebhookResponse = Static<typeof TestWebhookResponseSchema>

// Envelope que recibe el endpoint suscrito.
export const WebhookEnvelopeSchema = Type.Object({
  id: Type.String(),
  event: WebhookEventNameSchema,
  occurred_at: IsoDateTimeSchema,
  data: JsonRecordSchema,
})
export type WebhookEnvelope = Static<typeof WebhookEnvelopeSchema>

export const WebhookDeliverySchema = Type.Object({
  id: Type.String(),
  webhook_id: Type.String(),
  event: WebhookEventNameSchema,
  payload: JsonRecordSchema,
  attempts: Type.Integer({ minimum: 0 }),
  // Último código HTTP recibido (null si nunca respondió)
  last_status: Nullable(Type.Integer()),
  next_retry_at: Nullable(IsoDateTimeSchema),
})
export type WebhookDelivery = Static<typeof WebhookDeliverySchema>
